import React, { useState, useMemo, useEffect } from "react";
import { useGame } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/utils/i18n";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Coins, Loader2, Shield, Sword, Heart, Search, Layers, ChevronRight, X, User } from "lucide-react";
import soundManager from "@/utils/SoundManager";
import { resolveImageUrl } from "@/utils/imageUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RARITY_COSTS = {
  common: { gold: 50, dust: 20 },
  uncommon: { gold: 100, dust: 50 },
  rare: { gold: 250, dust: 150 },
  epic: { gold: 500, dust: 400 },
  legendary: { gold: 1000, dust: 1000 }
};

const RARITY_ORDER = ["common", "uncommon", "rare", "epic", "legendary"];

const RARITY_COLOR = {
  common: "#94a3b8",
  uncommon: "#22c55e",
  rare: "#3b82f6",
  epic: "#a855f7",
  legendary: "#f59e0b",
};

export default function Fusion() {
  const { game, setGame } = useGame();
  const { token, API } = useAuth();
  
  const [category, setCategory] = useState("allies"); // allies | objects | abilities
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [ritualActive, setRitualActive] = useState(false);
  const [ritualStep, setRitualStep] = useState(0);
  const [fusedCard, setFusedCard] = useState(null);
  
  const lang = game?.language || "en";
  const isIt = lang.startsWith("it");
  
  const character = game?.character || {};
  const currentGold = character.gold || 0;
  const currentDust = character.dust || 0;

  // Reset selection on category change
  useEffect(() => {
    setSelectedIds([]);
  }, [category]);

  // Loading text rotation during ritual
  useEffect(() => {
    if (!ritualActive) return;
    const interval = setInterval(() => {
      setRitualStep(prev => (prev + 1) % 3);
    }, 2000);
    return () => clearInterval(interval);
  }, [ritualActive]);

  // Map category to API card_type
  const cardType = useMemo(() => {
    if (category === "allies") return "ally";
    if (category === "objects") return "object";
    return "ability";
  }, [category]);

  // Get raw list of inventory cards
  const inventoryCards = useMemo(() => {
    return game?.inventory?.[category] || [];
  }, [game, category]);

  // Filter out basic cards & apply search query
  const eligibleCards = useMemo(() => {
    return inventoryCards.filter(card => {
      if (card.is_basic) return false;
      if (searchQuery.trim() === "") return true;
      return card.name?.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [inventoryCards, searchQuery]);

  // Map of selected cards
  const selectedCards = useMemo(() => {
    return eligibleCards.filter(c => selectedIds.includes(c.id));
  }, [eligibleCards, selectedIds]);

  // Cost calculation
  const { goldCost, dustCost, canAfford } = useMemo(() => {
    let gold = 0;
    let dust = 0;
    selectedCards.forEach(c => {
      const rarity = (c.rarity || "common").toLowerCase();
      const cost = RARITY_COSTS[rarity] || RARITY_COSTS.common;
      gold += cost.gold;
      dust += cost.dust;
    });
    return {
      goldCost: gold,
      dustCost: dust,
      canAfford: currentGold >= gold && currentDust >= dust
    };
  }, [selectedCards, currentGold, currentDust]);

  // Combined stats preview
  const previewStats = useMemo(() => {
    if (selectedCards.length < 2) return null;
    
    const attack = selectedCards.reduce((sum, c) => sum + (c.attack || 0), 0);
    const defense = selectedCards.reduce((sum, c) => sum + (c.defense || 0), 0);
    const hp = selectedCards.reduce((sum, c) => sum + (c.hp || 0), 0);
    const maxHp = selectedCards.reduce((sum, c) => sum + (c.max_hp || 0), 0);
    const effectValue = selectedCards.reduce((sum, c) => sum + (c.effect_value || 0), 0);
    const manaCost = Math.max(0, Math.floor(selectedCards.reduce((sum, c) => sum + (c.mana_cost || 0), 0) / selectedCards.length));
    
    // Rarity promotion
    const rarities = selectedCards.map(c => (c.rarity || "common").toLowerCase());
    const maxRarityIdx = Math.max(...rarities.map(r => RARITY_ORDER.indexOf(r)));
    const uniqueRarities = new Set(rarities);
    
    let fusedRarity = "common";
    let promoted = false;
    if (uniqueRarities.size === 1 && maxRarityIdx < RARITY_ORDER.length - 1) {
      fusedRarity = RARITY_ORDER[maxRarityIdx + 1];
      promoted = true;
    } else {
      fusedRarity = RARITY_ORDER[maxRarityIdx];
    }
    
    const isFoil = selectedCards.some(c => c.foil);
    
    return {
      attack,
      defense,
      hp,
      maxHp,
      effectValue,
      manaCost,
      rarity: fusedRarity,
      promoted,
      foil: isFoil
    };
  }, [selectedCards]);

  const handleCardToggle = (cardId) => {
    setSelectedIds(prev => {
      if (prev.includes(cardId)) {
        return prev.filter(id => id !== cardId);
      } else {
        return [...prev, cardId];
      }
    });
  };

  const startFusion = async () => {
    if (selectedIds.length < 2) {
      toast.error(isIt ? "Seleziona almeno 2 carte" : "Select at least 2 cards");
      return;
    }
    if (!canAfford) {
      toast.error(isIt ? "Risorse insufficienti per la fusione" : "Insufficient resources for fusion");
      return;
    }
    
    setRitualActive(true);
    setRitualStep(0);
    soundManager.playClick();
    
    try {
      const res = await axios.post(
        `${API}/game/${game.id}/card/fuse`,
        {
          card_ids: selectedIds,
          card_type: cardType
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 45000 // Fusion does AI details + image generation, give it extra time
        }
      );
      
      setGame(res.data.game);
      setFusedCard(res.data.card);
      soundManager.playLevelUp();
      toast.success(isIt ? "Fusione completata con successo!" : "Fusion completed successfully!");
    } catch (e) {
      toast.error(e.response?.data?.detail || (isIt ? "Rituale fallito, le energie si sono dissipate" : "Ritual failed, energies dissipated"));
    } finally {
      setRitualActive(false);
    }
  };

  const closeReveal = () => {
    setFusedCard(null);
    setSelectedIds([]);
  };

  const categories = [
    { id: "allies", label: isIt ? "Alleati" : "Allies" },
    { id: "objects", label: isIt ? "Oggetti" : "Objects" },
    { id: "abilities", label: isIt ? "Abilità" : "Abilities" }
  ];

  const loadingMessages = [
    isIt ? "Canalizzando le energie del vuoto..." : "Channeling void energies...",
    isIt ? "Fondendo le essenze delle carte..." : "Merging card essences...",
    isIt ? "Forgiando una nuova leggenda..." : "Forging a new legend..."
  ];

  return (
    <div className="py-2 sm:py-6 px-2 sm:px-4" data-testid="fusion-view">
      <div className="max-w-6xl mx-auto">
        
        {/* HUD/Header panel with player balances */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-6 bg-[#1a1a1d]/90 border border-[#cfb53b]/20 rounded-lg p-3 sm:p-4 backdrop-blur-sm">
          <div>
            <h2 className="font-heading text-lg sm:text-2xl text-[#cfb53b] tracking-wider uppercase flex items-center gap-2">
              <Layers className="text-[#cfb53b]" size={20} />
              {isIt ? "Rituale di Fusione" : "Fusion Ritual"}
            </h2>
            <p className="text-[10px] sm:text-xs text-[#94a3b8]">
              {isIt 
                ? "Sacrifica 2 o più carte per crearne una superiore con statistiche sommate." 
                : "Sacrifice 2 or more cards to create a superior one with combined statistics."}
            </p>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            {/* Gold Display */}
            <div className="flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-1.5 shadow-[0_0_8px_rgba(234,179,8,0.1)]">
              <Coins size={14} className="text-[#cfb53b]" />
              <span className="font-heading text-xs sm:text-sm text-[#cfb53b] font-bold">
                {currentGold.toLocaleString()}
              </span>
              <span className="text-[9px] uppercase tracking-wide text-[#94a3b8]">{isIt ? "Oro" : "Gold"}</span>
            </div>
            
            {/* Dust Display */}
            <div className="flex items-center gap-1.5 bg-purple-500/10 border border-purple-500/30 rounded-lg px-3 py-1.5 shadow-[0_0_8px_rgba(168,85,247,0.1)]">
              <Sparkles size={14} className="text-purple-400" />
              <span className="font-heading text-xs sm:text-sm text-purple-300 font-bold">
                {currentDust.toLocaleString()}
              </span>
              <span className="text-[9px] uppercase tracking-wide text-[#94a3b8]">{isIt ? "Polvere" : "Dust"}</span>
            </div>
          </div>
        </div>

        {/* Tab Selection & Search bar */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 mb-4">
          <div className="bg-black/50 border border-[#cfb53b]/20 p-1 flex rounded-lg">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setCategory(cat.id)}
                className={`font-heading text-xs px-4 py-2 rounded-md transition-all uppercase tracking-wider ${
                  category === cat.id
                    ? "bg-[#cfb53b]/15 text-[#cfb53b] font-bold shadow-[0_0_8px_rgba(207,181,59,0.1)]"
                    : "text-[#94a3b8] hover:text-white"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={isIt ? "Cerca carte..." : "Search cards..."}
              className="w-full bg-[#0a0a0c] border border-[#2d3748] focus:border-[#cfb53b]/40 rounded-lg pl-9 pr-3 py-2 text-xs text-white placeholder-slate-500 outline-none"
            />
          </div>
        </div>

        {/* Two Columns: Inventory & Ritual summary */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          
          {/* Column 1: Inventory Card Grid */}
          <div className="lg:col-span-7 bg-[#1a1a1d]/60 border border-[#2d3748] rounded-lg p-3 sm:p-4 backdrop-blur-sm">
            <h3 className="font-heading text-xs uppercase tracking-wider text-[#94a3b8] mb-3 flex items-center justify-between">
              <span>{isIt ? "Carte Disponibili" : "Available Cards"}</span>
              <span className="text-[10px] text-slate-500">
                {eligibleCards.length} {isIt ? "non-base trovate" : "non-basic found"}
              </span>
            </h3>

            {eligibleCards.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-[#2d3748] rounded-lg">
                <Layers className="mx-auto text-slate-600 mb-3" size={32} />
                <p className="text-xs text-[#94a3b8] font-heading">
                  {searchQuery 
                    ? (isIt ? "Nessuna carta corrisponde alla ricerca." : "No cards match the search query.")
                    : (isIt 
                      ? "Nessuna carta di questa categoria idonea per la fusione." 
                      : "No cards in this category are eligible for fusion.")}
                </p>
                <p className="text-[10px] text-slate-500 mt-1">
                  {isIt ? "(Le carte Base non possono essere fuse)" : "(Basic cards cannot be fused)"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 max-h-[460px] overflow-y-auto pr-1">
                {eligibleCards.map(card => {
                  const isSelected = selectedIds.includes(card.id);
                  const color = RARITY_COLOR[card.rarity] || "#94a3b8";
                  const hoverShadow = isSelected 
                    ? `shadow-[0_0_12px_${color}cc] border-[${color}]` 
                    : `border-slate-800 hover:border-slate-600`;
                  
                  return (
                    <button
                      key={card.id}
                      onClick={() => handleCardToggle(card.id)}
                      className={`relative flex flex-col justify-between bg-black/80 rounded-lg p-2 border transition-all text-left group ${hoverShadow}`}
                      style={{
                        borderColor: isSelected ? color : undefined,
                        boxShadow: isSelected ? `0 0 12px ${color}44` : undefined
                      }}
                    >
                      {/* Highlight Border Overlay */}
                      {isSelected && (
                        <div className="absolute inset-0 border border-white/20 rounded-lg pointer-events-none" />
                      )}
                      
                      <div>
                        {/* Title Bar */}
                        <div className="flex items-start justify-between gap-1 mb-1">
                          <span className="font-heading text-[10px] text-slate-200 truncate leading-tight group-hover:text-white">
                            {card.name}
                          </span>
                          {card.foil && (
                            <span className="text-[8px] text-[#fef08a] shrink-0" title="Foil">✨</span>
                          )}
                        </div>

                        {/* Rarity & Level */}
                        <div className="flex items-center justify-between text-[8px] mb-2 font-heading uppercase">
                          <span style={{ color }}>{t(lang, `rarity_${card.rarity}`)}</span>
                          <span className="text-slate-400">Lv.{card.level || 1}</span>
                        </div>
                      </div>

                      {/* Stat summary */}
                      <div className="flex items-center justify-between pt-1 border-t border-white/5 w-full">
                        <div className="flex items-center gap-2 text-[9px] font-heading text-slate-300">
                          {category === "allies" ? (
                            <>
                              <span className="flex items-center text-red-400"><Sword size={9} className="mr-0.5" />{card.attack}</span>
                              <span className="flex items-center text-green-400"><Heart size={9} className="mr-0.5" />{card.hp}</span>
                            </>
                          ) : category === "objects" ? (
                            <>
                              {card.attack > 0 && <span className="flex items-center text-red-400"><Sword size={9} className="mr-0.5" />+{card.attack}</span>}
                              {card.defense > 0 && <span className="flex items-center text-blue-400"><Shield size={9} className="mr-0.5" />+{card.defense}</span>}
                              {card.attack === 0 && card.defense === 0 && <span className="text-slate-500">-</span>}
                            </>
                          ) : (
                            <>
                              <span className="text-blue-300">{t(lang, 'mana')}: {card.mana_cost}</span>
                              {card.effect !== "none" && card.effect_value > 0 && (
                                <span className="text-yellow-400 font-bold">V.{card.effect_value}</span>
                              )}
                            </>
                          )}
                        </div>
                        
                        {/* Mana representation for non-spell tabs if cost exists */}
                        {category !== "abilities" && card.mana_cost > 0 && (
                          <span className="text-[8px] font-heading bg-blue-950/40 text-blue-300 px-1 rounded">
                            {card.mana_cost} Mana
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Column 2: Ritual Circle */}
          <div className="lg:col-span-5 bg-[#1a1a1d]/60 border border-[#2d3748] rounded-lg p-3 sm:p-4 flex flex-col justify-between backdrop-blur-sm min-h-[400px]">
            <div>
              <h3 className="font-heading text-xs uppercase tracking-wider text-[#94a3b8] mb-3">
                {isIt ? "Riepilogo Rituale" : "Ritual Summary"}
              </h3>
              
              {selectedCards.length === 0 ? (
                /* Empty state */
                <div className="flex flex-col items-center justify-center py-16 text-center text-slate-500 flex-1">
                  <div className="w-16 h-16 rounded-full border border-dashed border-slate-700 flex items-center justify-center mb-3 animate-pulse">
                    <Layers size={22} className="text-slate-600" />
                  </div>
                  <p className="text-[11px] font-heading px-4">
                    {isIt 
                      ? "Seleziona le carte a sinistra per caricarle nel cerchio rituale." 
                      : "Select cards on the left to charge them into the ritual circle."}
                  </p>
                </div>
              ) : selectedCards.length === 1 ? (
                /* One card state */
                <div className="flex flex-col items-center justify-center py-16 text-center text-slate-500 flex-1">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="font-heading text-xs text-[#cfb53b]">{selectedCards[0].name}</span>
                    <span className="text-[10px] text-slate-400">({isIt ? "caricata" : "charged"})</span>
                  </div>
                  <div className="w-10 h-10 rounded-full border border-dashed border-purple-500/40 flex items-center justify-center animate-spin mb-3">
                    <Sparkles size={14} className="text-purple-400/40" />
                  </div>
                  <p className="text-[11px] font-heading px-4 text-purple-300/80">
                    {isIt 
                      ? "Aggiungi almeno un'altra carta per incanalare la fusione." 
                      : "Add at least one more card to channel the fusion."}
                  </p>
                </div>
              ) : (
                /* Fusing State (Preview combined stats) */
                <div className="space-y-4">
                  {/* Visual combination queue */}
                  <div className="flex items-center flex-wrap gap-1.5 bg-black/45 p-2 rounded-lg border border-white/5">
                    {selectedCards.map((c, i) => (
                      <React.Fragment key={c.id}>
                        {i > 0 && <span className="text-[#cfb53b] text-xs font-bold font-heading">+</span>}
                        <div 
                          className="bg-[#121214] border border-[#cfb53b]/20 px-2 py-1 rounded text-[10px] font-heading text-slate-300"
                          style={{ borderColor: RARITY_COLOR[c.rarity] + "44" }}
                        >
                          {c.name}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>

                  {/* Arrow connector */}
                  <div className="flex justify-center text-purple-400 py-1">
                    <ChevronRight size={20} className="rotate-90" />
                  </div>

                  {/* Combined Preview Card display */}
                  {previewStats && (
                    <div className="bg-[#0f0f11] border border-purple-500/20 rounded-lg p-3 relative shadow-[0_0_15px_rgba(168,85,247,0.05)] overflow-hidden">
                      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#cfb53b] to-transparent opacity-60" />
                      
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-[10px] font-heading text-slate-400 uppercase tracking-wider mb-0.5">
                            {isIt ? "STATISTICHE DI FUSIONE" : "FUSION PREVIEW"}
                          </p>
                          <h4 className="font-heading text-sm text-[#cfb53b] flex items-center gap-1.5">
                            {isIt ? "Nuovo Artefatto Fuso" : "New Fused Artifact"}
                            {previewStats.foil && (
                              <span className="text-yellow-200 text-xs animate-pulse">✨</span>
                            )}
                          </h4>
                        </div>
                        
                        <div className="text-right">
                          <span 
                            className="text-[9px] font-heading px-2 py-0.5 rounded border uppercase"
                            style={{ 
                              color: RARITY_COLOR[previewStats.rarity], 
                              borderColor: RARITY_COLOR[previewStats.rarity] + "44",
                              backgroundColor: RARITY_COLOR[previewStats.rarity] + "11"
                            }}
                          >
                            {t(lang, `rarity_${previewStats.rarity}`)}
                          </span>
                          {previewStats.promoted && (
                            <p className="text-[8px] text-green-400 font-heading mt-1 uppercase tracking-tight">
                              ↑ {isIt ? "Rarità Promossa!" : "Rarity Promoted!"}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Display Fused Stats in grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 py-2 border-y border-white/5 my-2">
                        {category === "allies" && (
                          <>
                            <div className="bg-[#18181b]/50 p-1.5 rounded text-center">
                              <span className="text-[8px] text-slate-400 block font-heading uppercase">ATT</span>
                              <span className="font-heading text-xs text-red-400 font-bold">{previewStats.attack}</span>
                            </div>
                            <div className="bg-[#18181b]/50 p-1.5 rounded text-center">
                              <span className="text-[8px] text-slate-400 block font-heading uppercase">PV MAXX</span>
                              <span className="font-heading text-xs text-green-400 font-bold">{previewStats.hp}</span>
                            </div>
                          </>
                        )}
                        {category === "objects" && (
                          <>
                            <div className="bg-[#18181b]/50 p-1.5 rounded text-center">
                              <span className="text-[8px] text-slate-400 block font-heading uppercase">ATT AGGIUNTO</span>
                              <span className="font-heading text-xs text-red-400 font-bold">+{previewStats.attack}</span>
                            </div>
                            <div className="bg-[#18181b]/50 p-1.5 rounded text-center">
                              <span className="text-[8px] text-slate-400 block font-heading uppercase">DIF AGGIUNTA</span>
                              <span className="font-heading text-xs text-blue-400 font-bold">+{previewStats.defense}</span>
                            </div>
                          </>
                        )}
                        {category === "abilities" && (
                          <div className="bg-[#18181b]/50 p-1.5 rounded text-center">
                            <span className="text-[8px] text-slate-400 block font-heading uppercase">VALORE EFFETTO</span>
                            <span className="font-heading text-xs text-yellow-400 font-bold">{previewStats.effectValue}</span>
                          </div>
                        )}
                        
                        {/* Mana Cost */}
                        <div className="bg-[#18181b]/50 p-1.5 rounded text-center">
                          <span className="text-[8px] text-slate-400 block font-heading uppercase">{isIt ? "MANA MEDIO" : "MANA AVERAGE"}</span>
                          <span className="font-heading text-xs text-blue-300 font-bold">{previewStats.manaCost}</span>
                        </div>
                      </div>

                      {/* Info callout */}
                      <p className="text-[9px] text-[#94a3b8] italic text-center">
                        {isIt 
                          ? "* Il nome finale, la descrizione e l'illustrazione saranno forgiati dall'IA." 
                          : "* The final name, description, and artwork will be forged by the AI."}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Price section & CTA */}
            {selectedCards.length >= 2 && (
              <div className="mt-4 pt-4 border-t border-slate-800 space-y-3">
                <div className="flex items-center justify-between text-xs font-heading">
                  <span className="text-slate-400">{isIt ? "Prezzo Fusione" : "Fusion Cost"}:</span>
                  <div className="flex items-center gap-3">
                    {/* Gold Cost */}
                    <span className={`flex items-center gap-1 font-bold ${currentGold >= goldCost ? 'text-[#cfb53b]' : 'text-red-400 animate-pulse'}`}>
                      <Coins size={11} /> {goldCost}
                    </span>
                    {/* Dust Cost */}
                    <span className={`flex items-center gap-1 font-bold ${currentDust >= dustCost ? 'text-purple-300' : 'text-red-400 animate-pulse'}`}>
                      <Sparkles size={11} /> {dustCost}
                    </span>
                  </div>
                </div>

                {!canAfford && (
                  <p className="text-[10px] text-red-400 text-center font-heading">
                    ⚠️ {isIt ? "Non hai abbastanza Oro o Polvere!" : "Not enough Gold or Dust!"}
                  </p>
                )}

                <button
                  disabled={!canAfford}
                  onClick={startFusion}
                  className={`w-full py-2.5 rounded-lg text-xs font-heading uppercase tracking-widest font-bold transition-all shadow-lg ${
                    canAfford
                      ? "bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-purple-500/10 cursor-pointer"
                      : "bg-[#2d3748] text-slate-500 cursor-not-allowed"
                  }`}
                >
                  {isIt ? "Avvia Rito di Fusione" : "Begin Fusion Ritual"}
                </button>
              </div>
            )}
          </div>

        </div>

      </div>

      {/* RITUAL OVERLAY ANIMATION */}
      {ritualActive && (
        <div className="fixed inset-0 z-[250] bg-black/95 flex flex-col items-center justify-center p-4">
          {/* Radial Glowing Core */}
          <div className="absolute w-[300px] h-[300px] rounded-full bg-purple-500/5 blur-[80px] pointer-events-none" />
          
          {/* Rotating Magical Sigil (SVG) */}
          <div className="w-48 h-48 mb-8 relative flex items-center justify-center">
            <svg 
              viewBox="0 0 100 100" 
              className="w-full h-full text-purple-500/40 animate-[spin_10s_linear_infinite]"
            >
              <circle cx="50" cy="50" r="48" fill="none" stroke="currentColor" strokeWidth="0.75" strokeDasharray="3 3" />
              <circle cx="50" cy="50" r="44" fill="none" stroke="currentColor" strokeWidth="1" />
              <polygon points="50,6 92,78 8,78" fill="none" stroke="currentColor" strokeWidth="0.75" />
              <polygon points="50,94 92,22 8,22" fill="none" stroke="currentColor" strokeWidth="0.75" />
              <circle cx="50" cy="50" r="18" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </svg>
            <div className="absolute w-12 h-12 rounded-full bg-black flex items-center justify-center border border-[#cfb53b]/40 shadow-[0_0_15px_rgba(168,85,247,0.3)] animate-pulse">
              <Sparkles size={20} className="text-[#cfb53b]" />
            </div>
          </div>

          {/* Incantation/Ritual status message */}
          <div className="text-center space-y-2 max-w-sm">
            <h3 className="font-heading text-sm text-[#cfb53b] tracking-[0.2em] uppercase animate-pulse">
              {isIt ? "FUSIONE IN CORSO" : "FUSION IN PROGRESS"}
            </h3>
            <p className="text-xs text-[#94a3b8] font-heading h-6">
              {loadingMessages[ritualStep]}
            </p>
            <div className="flex justify-center pt-2">
              <Loader2 size={16} className="text-[#cfb53b] animate-spin" />
            </div>
          </div>
        </div>
      )}

      {/* RESULT REVEAL MODAL */}
      {fusedCard && (
        <div 
          className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={closeReveal}
        >
          {/* Glowing Aura back */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at 50% 50%, ${RARITY_COLOR[fusedCard.rarity] || "#cfb53b"}22 0%, transparent 60%)`
            }}
          />

          <div 
            className="relative flex flex-col items-center gap-5 max-w-sm w-full animate-fadeInUp"
            onClick={e => e.stopPropagation()}
          >
            
            {/* Exit button */}
            <button
              onClick={closeReveal}
              className="absolute -top-12 right-0 sm:right-4 w-9 h-9 rounded-full bg-[#1a1a1d] border border-white/10 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>

            {/* Header announcement */}
            <div className="text-center">
              <p className="text-[10px] font-heading uppercase tracking-[0.3em] text-[#cfb53b] mb-1">
                {isIt ? "RITUALE COMPLETATO!" : "RITUAL COMPLETED!"}
              </p>
              <h2 className="font-heading text-xl sm:text-2xl text-[#e2e8f0]">
                {isIt ? "Forgiata Nuova Carta" : "New Card Forged"}
              </h2>
            </div>

            {/* Premium MTG Card Display */}
            {(() => {
              const rColor = RARITY_COLOR[fusedCard.rarity] || "#94a3b8";
              const typeClass = fusedCard.type === "ally" ? "card-ally" : fusedCard.type === "ability" ? "card-ability" : "card-object";
              const rarityClass = fusedCard.rarity ? `rarity-${fusedCard.rarity.toLowerCase()}` : "rarity-common";
              const hasPlaque = fusedCard.type === "ally" || (fusedCard.type === "object" && ((fusedCard.attack || 0) > 0 || (fusedCard.defense || 0) > 0));
              
              return (
                <div 
                  className={`mtg-card ${typeClass} ${rarityClass} ${fusedCard.foil ? "card-foil" : ""} shadow-[0_0_30px_rgba(168,85,247,0.25)]`}
                  style={{ width: '280px', height: '390px', padding: '6px' }}
                >
                  <div className="mtg-inner-frame" style={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    
                    {/* Header */}
                    <div className="mtg-header-bar" style={{ height: '24px', padding: '2px 6px' }}>
                      <span className="mtg-card-title text-[11px]">{fusedCard.name}</span>
                      <div className="mtg-header-right">
                        {fusedCard.level > 1 && (
                          <span className="mtg-level-badge text-[8px]">Lv{fusedCard.level}</span>
                        )}
                        {fusedCard.mana_cost > 0 && (
                          <div className="mtg-mana-gem" style={{ width: '14px', height: '14px', fontSize: '9px' }}>
                            <span>{fusedCard.mana_cost}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Art */}
                    <div className="mtg-art-frame" style={{ flex: 1 }}>
                      {fusedCard.image_url ? (
                        <img 
                          src={resolveImageUrl(fusedCard.image_url, BACKEND_URL)} 
                          alt={fusedCard.name} 
                          className="mtg-art-image" 
                          onError={(e) => { e.target.style.display = 'none'; }} 
                        />
                      ) : (
                        <div className="mtg-no-art">
                          <Sparkles size={24} style={{ color: rColor }} />
                        </div>
                      )}
                    </div>

                    {/* Type line */}
                    <div className="mtg-type-line" style={{ padding: '2px 6px', height: '20px' }}>
                      <span className="mtg-type-text text-[9px] uppercase font-heading">
                        {t(lang, `type_${fusedCard.type}`)}
                      </span>
                      <span className="text-[10px] font-heading" style={{ color: rColor }}>✦</span>
                    </div>

                    {/* Rules Box */}
                    <div className="mtg-rules-box" style={{ height: '88px', padding: '4px 6px', overflow: 'hidden' }}>
                      <div className="mtg-description" style={{ fontSize: '10px' }}>
                        {fusedCard.effect && fusedCard.effect !== "none" && fusedCard.effect_value > 0 && (
                          <div className="mb-0.5 font-heading text-[8px] uppercase tracking-wider">
                            {fusedCard.effect === "damage" && <span className="text-red-500">{isIt ? "DANNO" : "DMG"}: {fusedCard.effect_value}</span>}
                            {fusedCard.effect === "heal" && <span className="text-green-500">{isIt ? "CURA" : "HEAL"}: {fusedCard.effect_value}</span>}
                            {fusedCard.effect === "buff" && <span className="text-amber-500">BUFF: +{fusedCard.effect_value}</span>}
                            {fusedCard.effect === "debuff" && <span className="text-purple-500">DEBUFF: -{fusedCard.effect_value}</span>}
                          </div>
                        )}
                        <p className="leading-snug">{fusedCard.description}</p>
                      </div>
                    </div>

                    {/* Stat Plaque */}
                    {hasPlaque && (
                      <div className="mtg-stat-plaque" style={{ bottom: '4px', right: '4px', fontSize: '8px', padding: '1px 4px' }}>
                        {fusedCard.type === "ally" ? (
                          <>
                            <span>{fusedCard.attack}</span> / <span className="text-emerald-700">{fusedCard.hp}</span>
                          </>
                        ) : (
                          <>
                            {fusedCard.attack > 0 && <span>+{fusedCard.attack}</span>}
                            {fusedCard.attack > 0 && fusedCard.defense > 0 && <span>/</span>}
                            {fusedCard.defense > 0 && <span>+{fusedCard.defense}</span>}
                          </>
                        )}
                      </div>
                    )}

                  </div>
                </div>
              );
            })()}

            {/* CTA Close */}
            <button
              onClick={closeReveal}
              className="w-full max-w-[280px] py-2 bg-[#cfb53b] hover:bg-[#d4af37] text-black font-heading text-xs rounded-lg uppercase font-bold tracking-widest transition-all shadow-[0_4px_12px_rgba(207,181,59,0.2)] cursor-pointer text-center"
            >
              {isIt ? "Accetta e Chiudi" : "Accept & Close"}
            </button>
            
          </div>
        </div>
      )}

    </div>
  );
}
